import { LitElement, html, css, nothing } from 'lit';
import { when } from 'lit/directives/when.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

const ICONS = {
  chat: html`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`,
  close: html`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`,
  send: html`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>`,
  attach: html`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>`,
  file: html`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>`,
  check: html`<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`,
  error: html`<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>`,
  arrowDown: html`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>`,
  ai: html`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l1.8 4.2L18 9l-4.2 1.8L12 15l-1.8-4.2L6 9l4.2-1.8z"/><path d="M18.5 15.5l.8 1.9 1.9.8-1.9.8-.8 1.9-.8-1.9-1.9-.8 1.9-.8z"/></svg>`,
};

const LABELS = {
  it: {
    chatRegion: 'Widget di chat',
    chatDialog: 'Chat',
    openChat: 'Apri chat',
    closeChat: 'Minimizza chat',
    messagesLog: 'Messaggi',
    emptyText: 'Poni una domanda per iniziare.',
    typing: 'sta scrivendo...',
    online: 'Online',
    offline: 'Offline',
    connecting: 'Connessione...',
    ready: 'Pronto',
    disconnected: 'Disconnesso — riconnessione al prossimo messaggio',
    messagePlaceholder: 'Scrivi un messaggio...',
    messageLabel: 'Messaggio',
    attach: 'Allega file',
    send: 'Invia',
    removeFile: 'Rimuovi {file}',
    scrollToBottom: 'Vai agli ultimi messaggi',
    error: 'Errore',
    aiBadgeTitle: 'Stai interagendo con un assistente basato su intelligenza artificiale',
    aiDisclosure: 'Assistente virtuale basato su IA. Stai interagendo con un sistema di intelligenza artificiale. Le risposte possono contenere errori. Verifica le informazioni importanti.',
  },
  en: {
    chatRegion: 'Chat widget',
    chatDialog: 'Chat',
    openChat: 'Open chat',
    closeChat: 'Minimize chat',
    messagesLog: 'Messages',
    emptyText: 'Ask a question to get started.',
    typing: 'is typing...',
    online: 'Online',
    offline: 'Offline',
    connecting: 'Connecting...',
    ready: 'Ready',
    disconnected: 'Disconnected — reconnecting on next message',
    messagePlaceholder: 'Type a message...',
    messageLabel: 'Message',
    attach: 'Attach file',
    send: 'Send',
    removeFile: 'Remove {file}',
    scrollToBottom: 'Jump to latest messages',
    error: 'Error',
    aiBadgeTitle: 'You are interacting with an AI-based assistant',
    aiDisclosure: 'AI-based virtual assistant. You are interacting with an artificial intelligence system. Responses may contain errors. Verify important information.',
  },
  de: {
    chatRegion: 'Chat-Widget',
    chatDialog: 'Chat',
    openChat: 'Chat öffnen',
    closeChat: 'Chat minimieren',
    messagesLog: 'Nachrichten',
    emptyText: 'Stelle eine Frage um zu beginnen.',
    typing: 'schreibt...',
    online: 'Online',
    offline: 'Offline',
    connecting: 'Verbindung...',
    ready: 'Bereit',
    disconnected: 'Getrennt — Wiederverbindung bei der nächsten Nachricht',
    messagePlaceholder: 'Nachricht schreiben...',
    messageLabel: 'Nachricht',
    attach: 'Datei anhängen',
    send: 'Senden',
    removeFile: '{file} entfernen',
    scrollToBottom: 'Zu den neuesten Nachrichten springen',
    error: 'Fehler',
    aiBadgeTitle: 'Sie interagieren mit einem KI-gestützten Assistenten',
    aiDisclosure: 'KI-gestützter virtueller Assistent. Sie interagieren mit einem KI-System. Antworten können Fehler enthalten. Bitte wichtige Informationen überprüfen.',
  },
  fr: {
    chatRegion: 'Widget de chat',
    chatDialog: 'Chat',
    openChat: 'Ouvrir le chat',
    closeChat: 'Réduire le chat',
    messagesLog: 'Messages',
    emptyText: 'Posez une question pour commencer.',
    typing: 'écrit...',
    online: 'En ligne',
    offline: 'Hors ligne',
    connecting: 'Connexion...',
    ready: 'Prêt',
    disconnected: 'Déconnecté — reconnexion au prochain message',
    messagePlaceholder: 'Écrivez un message...',
    messageLabel: 'Message',
    attach: 'Joindre un fichier',
    send: 'Envoyer',
    removeFile: 'Retirer {file}',
    scrollToBottom: 'Aller aux derniers messages',
    error: 'Erreur',
    aiBadgeTitle: 'Vous interagissez avec un assistant basé sur l’intelligence artificielle',
    aiDisclosure: 'Assistant virtuel basé sur l’IA. Vous interagissez avec un système d’intelligence artificielle. Les réponses peuvent contenir des erreurs. Vérifiez les informations importantes.',
  },
  es: {
    chatRegion: 'Widget de chat',
    chatDialog: 'Chat',
    openChat: 'Abrir chat',
    closeChat: 'Minimizar chat',
    messagesLog: 'Mensajes',
    emptyText: 'Haz una pregunta para empezar.',
    typing: 'está escribiendo...',
    online: 'En línea',
    offline: 'Desconectado',
    connecting: 'Conectando...',
    ready: 'Listo',
    disconnected: 'Desconectado — reconexión en el próximo mensaje',
    messagePlaceholder: 'Escribe un mensaje...',
    messageLabel: 'Mensaje',
    attach: 'Adjuntar archivo',
    send: 'Enviar',
    removeFile: 'Quitar {file}',
    scrollToBottom: 'Ir a los últimos mensajes',
    error: 'Error',
    aiBadgeTitle: 'Estás interactuando con un asistente basado en inteligencia artificial',
    aiDisclosure: 'Asistente virtual basado en IA. Estás interactuando con un sistema de inteligencia artificial. Las respuestas pueden contener errores. Verifica la información importante.',
  },
};

class AiChat extends LitElement {
  static properties = {
    wsUrl: { type: String, attribute: 'ws-url' },
    apiUrl: { type: String, attribute: 'api-url' },
    botName: { type: String, attribute: 'bot-name' },
    botAvatar: { type: String, attribute: 'bot-avatar' },
    theme: { type: String, reflect: true },
    placeholder: { type: String },
    position: { type: String },
    maxFileSize: { type: Number, attribute: 'max-file-size' },
    allowedFileTypes: { type: String, attribute: 'allowed-file-types' },
    initialMessages: { type: String, attribute: 'initial-messages' },
    open: { type: Boolean, reflect: true },
    height: { type: String },
    width: { type: String },
    primaryColor: { type: String, attribute: 'primary-color' },
    bgColor: { type: String, attribute: 'bg-color' },
    textColor: { type: String, attribute: 'text-color' },
    userBubbleColor: { type: String, attribute: 'user-bubble-color' },
    botBubbleColor: { type: String, attribute: 'bot-bubble-color' },
    lang: { type: String },
    labels: { type: String },
    _messages: { state: true },
    _isTyping: { state: true },
    _unreadCount: { state: true },
    _wsState: { state: true },
    _inputValue: { state: true },
    _files: { state: true },
    _isAtBottom: { state: true },
  };

  constructor() {
    super();
    this.wsUrl = '';
    this.apiUrl = '';
    this.botName = 'Assistant';
    this.botAvatar = '';
    this.theme = 'light';
    this.placeholder = '';
    this.position = 'bottom-right';
    this.maxFileSize = 10;
    this.allowedFileTypes = '';
    this.initialMessages = '';
    this.open = false;
    this.height = '600px';
    this.width = '380px';
    this.primaryColor = '';
    this.bgColor = '';
    this.textColor = '';
    this.userBubbleColor = '';
    this.botBubbleColor = '';
    this.lang = '';
    this.labels = '';

    this._messages = [];
    this._isTyping = false;
    this._unreadCount = 0;
    this._wsState = 'idle';
    this._inputValue = '';
    this._files = [];
    this._isAtBottom = true;
    this._messageIds = new Set();
    this._sessionId = null;
    this._sessionPromise = null;
    this._ws = null;
    this._wsOpenPromise = null;
    this._wsOpenReject = null;
    this._wsUsed = false;
  }

  static styles = css`
    :host {
      --accent: var(--ai-chat-primary, #3b82f6);
      --accent-hover: var(--ai-chat-primary-hover, color-mix(in srgb, var(--accent) 80%, #000));
      --accent-light: color-mix(in srgb, var(--accent) 15%, transparent);
      --bg: var(--ai-chat-bg, #ffffff);
      --bg-secondary: var(--ai-chat-bg-secondary, #f8fafc);
      --text: var(--ai-chat-text, #0f172a);
      --text-secondary: var(--ai-chat-text-secondary, #64748b);
      --text-muted: var(--ai-chat-text-muted, #94a3b8);
      --user-bubble: var(--ai-chat-user-bubble, var(--accent));
      --user-text: var(--ai-chat-user-text, #ffffff);
      --bot-bubble: var(--ai-chat-bot-bubble, #f1f5f9);
      --bot-text: var(--ai-chat-bot-text, var(--text));
      --border: var(--ai-chat-border, #e2e8f0);
      --shadow: var(--ai-chat-shadow, 0 4px 24px rgba(0,0,0,0.12));
      --radius: var(--ai-chat-radius, 12px);
      --font: var(--ai-chat-font-family, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif);
      --pos-bottom: var(--ai-chat-position-bottom, 24px);
      --pos-right: var(--ai-chat-position-right, 24px);
      --chat-width: var(--ai-chat-width, 380px);
      --chat-height: var(--ai-chat-height, 600px);
      --transition: 280ms cubic-bezier(0.4, 0, 0.2, 1);
      all: initial;
      display: block;
      font-family: var(--font);
      color: var(--text);
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    :host([theme="dark"]) {
      --bg: #0f172a;
      --bg-secondary: #1e293b;
      --text: #f1f5f9;
      --text-secondary: #94a3b8;
      --text-muted: #64748b;
      --bot-bubble: #1e293b;
      --bot-text: #f1f5f9;
      --border: #334155;
      --shadow: 0 4px 24px rgba(0,0,0,0.4);
    }

    :host([position="bottom-left"]) {
      --pos-right: auto;
      --pos-left: var(--ai-chat-position-left, 24px);
    }

    *, *::before, *::after { box-sizing: border-box; }

    .wrapper {
      position: fixed;
      bottom: var(--pos-bottom);
      right: var(--pos-right);
      left: var(--pos-left, auto);
      z-index: 2147483647;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 12px;
      font-family: var(--font);
    }

    .toggle {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: var(--accent);
      color: #fff;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 16px rgba(0,0,0,0.2);
      transition: transform var(--transition), box-shadow var(--transition), background var(--transition);
      position: relative;
      flex-shrink: 0;
    }
    .toggle:hover {
      transform: scale(1.05);
      background: var(--accent-hover);
      box-shadow: 0 6px 20px rgba(0,0,0,0.25);
    }
    .toggle:active { transform: scale(0.95); }
    .toggle svg { width: 24px; height: 24px; }
    .toggle .icon-open,
    .toggle.is-open .icon-closed { display: none; }
    .toggle.is-open .icon-open { display: block; }

    .badge {
      position: absolute;
      top: -4px;
      right: -4px;
      min-width: 20px;
      height: 20px;
      padding: 0 5px;
      border-radius: 10px;
      background: #ef4444;
      color: #fff;
      font-size: 11px;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
      animation: badgePop 300ms cubic-bezier(0.68, -0.55, 0.27, 1.55);
    }
    .badge.pulse { animation: badgePulse 2s ease-in-out infinite; }

    .toggle .ai-chip {
      position: absolute;
      bottom: -3px;
      left: -3px;
      background: #fff;
      color: var(--accent);
      border: 1px solid currentColor;
      border-radius: 999px;
      font-size: 9px;
      font-weight: 800;
      letter-spacing: 0.05em;
      padding: 1px 5px;
      pointer-events: none;
    }
    @keyframes badgePop {
      0% { transform: scale(0); }
      100% { transform: scale(1); }
    }
    @keyframes badgePulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.15); }
    }

    .window {
      position: fixed;
      bottom: calc(var(--pos-bottom) + 68px);
      right: var(--pos-right);
      left: var(--pos-left, auto);
      width: var(--chat-width);
      height: var(--chat-height);
      max-height: calc(100vh - 120px);
      max-width: calc(100vw - 48px);
      background: var(--bg);
      border-radius: 16px;
      box-shadow: var(--shadow);
      border: 1px solid var(--border);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      transform-origin: bottom right;
      animation: windowIn var(--transition) forwards;
      z-index: 2147483646;
    }
    :host([position="bottom-left"]) .window { transform-origin: bottom left; }
    @keyframes windowIn {
      from { opacity: 0; transform: translateY(16px) scale(0.96); pointer-events: none; }
      to   { opacity: 1; transform: translateY(0) scale(1); pointer-events: auto; }
    }

    .header {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 16px 20px;
      border-bottom: 1px solid var(--border);
      background: var(--bg);
      flex-shrink: 0;
    }
    .header-avatar {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: var(--accent-light);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      font-weight: 600;
      color: var(--accent);
      overflow: hidden;
      flex-shrink: 0;
    }
    .header-avatar img { width: 100%; height: 100%; object-fit: cover; }
    .header-info { flex: 1; min-width: 0; }
    .header-title {
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
    }
    .header-title .header-name { min-width: 0; }
    .header-name {
      font-size: 15px;
      font-weight: 600;
      color: var(--text);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .header-status {
      font-size: 12px;
      color: var(--text-muted);
      display: flex;
      align-items: center;
      gap: 4px;
    }
    .header-status .dot {
      width: 6px; height: 6px; border-radius: 50%; display: inline-block;
    }
    .header-status .dot.online { background: #22c55e; }
    .header-status .dot.typing { background: var(--accent); animation: dotPulse 1.2s ease-in-out infinite; }
    .header-status .dot.offline { background: var(--text-muted); }

    .ai-badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 2px 7px;
      border-radius: 999px;
      background: var(--accent-light);
      color: var(--accent);
      border: 1px solid color-mix(in srgb, var(--accent) 35%, transparent);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.05em;
      flex-shrink: 0;
      cursor: default;
    }
    .ai-badge svg { width: 11px; height: 11px; }

    .ai-disclosure {
      display: flex;
      align-items: flex-start;
      gap: 6px;
      padding: 7px 16px;
      font-size: 11px;
      line-height: 1.4;
      color: var(--text-secondary);
      background: var(--bg-secondary);
      border-top: 1px solid var(--border);
      flex-shrink: 0;
    }
    .ai-disclosure svg {
      width: 13px;
      height: 13px;
      color: var(--accent);
      flex-shrink: 0;
      margin-top: 1px;
    }
    @keyframes dotPulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.3; }
    }
    .header-actions { display: flex; gap: 4px; flex-shrink: 0; }
    .header-btn {
      width: 32px; height: 32px;
      border-radius: 8px;
      background: transparent;
      color: var(--text-secondary);
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background var(--transition);
    }
    .header-btn:hover { background: var(--bot-bubble); color: var(--text); }
    .header-btn svg { width: 18px; height: 18px; }

    .messages {
      flex: 1;
      overflow-y: auto;
      padding: 16px 20px;
      display: flex;
      flex-direction: column;
      gap: 8px;
      scroll-behavior: smooth;
      background: var(--bg-secondary);
      overscroll-behavior: contain;
    }
    .messages::-webkit-scrollbar { width: 4px; }
    .messages::-webkit-scrollbar-track { background: transparent; }
    .messages::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }

    .msg-group {
      display: flex;
      flex-direction: column;
      gap: 4px;
      max-width: 85%;
    }
    .msg-group.user { align-self: flex-end; align-items: flex-end; }
    .msg-group.bot { align-self: flex-start; align-items: flex-start; }

    .msg-bubble {
      padding: 10px 14px;
      border-radius: 16px;
      font-size: 14px;
      line-height: 1.5;
      word-wrap: break-word;
      position: relative;
    }
    .msg-group.user .msg-bubble {
      background: var(--user-bubble);
      color: var(--user-text);
      border-bottom-right-radius: 4px;
    }
    .msg-group.bot .msg-bubble {
      background: var(--bot-bubble);
      color: var(--bot-text);
      border-bottom-left-radius: 4px;
    }
    .msg-bubble p { margin: 0; }
    .msg-bubble p + p { margin-top: 8px; }

    .msg-time {
      font-size: 10px;
      color: var(--text-muted);
      padding: 0 4px;
      margin-top: 2px;
    }
    .msg-status {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      font-size: 11px;
      margin-top: 2px;
    }
    .msg-status.sent { color: var(--text-muted); }
    .msg-status.error { color: #ef4444; }
    .msg-status svg { width: 12px; height: 12px; }

    .msg-files { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
    .msg-file {
      display: flex; align-items: center; gap: 6px;
      padding: 4px 8px; border-radius: 8px;
      background: rgba(0,0,0,0.06);
      font-size: 12px; cursor: pointer;
      text-decoration: none; color: inherit;
      transition: background var(--transition);
    }
    .msg-file:hover { background: rgba(0,0,0,0.1); }
    .msg-file svg { width: 14px; height: 14px; flex-shrink: 0; }
    .msg-file img { width: 40px; height: 40px; border-radius: 6px; object-fit: cover; }
    .msg-group.user .msg-file { background: rgba(255,255,255,0.15); }
    .msg-group.user .msg-file:hover { background: rgba(255,255,255,0.25); }

    .msg-elements {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 8px;
    }
    .msg-element {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 6px 12px;
      border-radius: 10px;
      font-size: 13px;
      text-decoration: none;
      cursor: pointer;
      transition: all var(--transition);
      border: 1px solid var(--border);
      background: transparent;
      color: var(--text);
      font-family: var(--font);
      line-height: 1.3;
      word-break: break-word;
    }
    .msg-element svg {
      width: 14px;
      height: 14px;
      flex-shrink: 0;
    }
    .msg-element:hover { border-color: var(--accent); color: var(--accent); }
    .msg-element--quick-reply {
      background: var(--accent-light);
      border-color: var(--accent);
      color: var(--accent);
      font-weight: 500;
    }
    .msg-element--quick-reply:hover {
      background: var(--accent);
      color: #fff;
    }
    .msg-element--web-link {
      display: inline-flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 1px;
      padding: 6px 12px;
      line-height: 1.4;
    }
    .msg-element--web-link { color: var(--accent); }
    .msg-element--web-link:hover { background: var(--accent-light); }
    .msg-element__domain {
      font-size: 10px;
      opacity: 0.6;
      text-transform: lowercase;
      font-weight: 500;
      letter-spacing: 0.02em;
    }
    .msg-element__label { font-size: 13px; }
    .msg-element--image {
      padding: 4px;
      border-style: dashed;
    }
    .msg-element--image img {
      max-width: 200px;
      max-height: 200px;
      border-radius: 6px;
      object-fit: cover;
    }
    .msg-element--system {
      opacity: 0.6;
      font-size: 11px;
      font-style: italic;
      cursor: default;
      border: none;
      padding: 4px 8px;
    }

    .typing {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 20px;
      animation: fadeIn 200ms ease-out;
    }
    .typing-dots {
      display: flex;
      gap: 3px;
      padding: 10px 14px;
      background: var(--bot-bubble);
      border-radius: 16px;
      border-bottom-left-radius: 4px;
    }
    .typing-dots span {
      width: 8px; height: 8px; border-radius: 50%;
      background: var(--text-muted);
      animation: typingBounce 1.4s ease-in-out infinite;
    }
    .typing-dots span:nth-child(2) { animation-delay: 0.2s; }
    .typing-dots span:nth-child(3) { animation-delay: 0.4s; }
    @keyframes typingBounce {
      0%, 60%, 100% { transform: translateY(0); }
      30% { transform: translateY(-6px); }
    }
    .typing-label { font-size: 12px; color: var(--text-muted); }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }

    .empty-state {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 40px 20px;
      text-align: center;
      color: var(--text-muted);
    }
    .empty-state svg { width: 48px; height: 48px; opacity: 0.4; }
    .empty-state h3 { margin: 0; font-size: 16px; font-weight: 600; color: var(--text-secondary); }
    .empty-state p { margin: 0; font-size: 13px; max-width: 240px; }

    .scroll-btn {
      position: absolute;
      bottom: 80px;
      left: 50%;
      transform: translateX(-50%);
      width: 32px; height: 32px;
      border-radius: 50%;
      background: var(--bg);
      border: 1px solid var(--border);
      color: var(--text-secondary);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: var(--shadow);
      transition: opacity var(--transition), transform var(--transition);
      opacity: 0;
      pointer-events: none;
    }
    .scroll-btn.visible { opacity: 1; pointer-events: auto; }
    .scroll-btn:hover { transform: translateX(-50%) scale(1.1); }
    .scroll-btn svg { width: 16px; height: 16px; }

    .input-area {
      padding: 12px 16px;
      border-top: 1px solid var(--border);
      background: var(--bg);
      flex-shrink: 0;
    }
    .file-previews { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
    .file-chip {
      display: flex; align-items: center; gap: 6px;
      padding: 6px 10px;
      background: var(--bot-bubble);
      border-radius: 8px; font-size: 12px;
      color: var(--text);
      max-width: 200px;
    }
    .file-chip img { width: 28px; height: 28px; border-radius: 4px; object-fit: cover; }
    .file-chip .file-icon {
      width: 28px; height: 28px;
      display: flex; align-items: center; justify-content: center;
      background: var(--accent-light); border-radius: 4px; color: var(--accent);
    }
    .file-chip .file-icon svg { width: 14px; height: 14px; }
    .file-chip .file-info { min-width: 0; flex: 1; }
    .file-chip .file-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-weight: 500; }
    .file-chip .file-size { font-size: 10px; color: var(--text-muted); }
    .file-chip .file-remove {
      width: 18px; height: 18px; border-radius: 50%;
      border: none; background: transparent;
      color: var(--text-muted); cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0; padding: 0;
      transition: background var(--transition), color var(--transition);
    }
    .file-chip .file-remove:hover { background: rgba(0,0,0,0.08); color: var(--text); }

    .input-row { display: flex; align-items: flex-end; gap: 8px; }
    .input-row textarea {
      flex: 1; resize: none;
      border: 1px solid var(--border);
      border-radius: 12px;
      padding: 10px 14px;
      font-family: var(--font); font-size: 14px; line-height: 1.5;
      color: var(--text); background: var(--bg-secondary);
      outline: none;
      transition: border-color var(--transition), box-shadow var(--transition);
      max-height: 120px; min-height: 42px;
    }
    .input-row textarea::placeholder { color: var(--text-muted); }
    .input-row textarea:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-light); }
    .input-actions { display: flex; gap: 4px; flex-shrink: 0; }
    .input-btn {
      width: 42px; height: 42px; border-radius: 12px;
      border: none; background: transparent;
      color: var(--text-secondary); cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      transition: background var(--transition), color var(--transition);
      flex-shrink: 0;
    }
    .input-btn:hover { background: var(--bot-bubble); color: var(--text); }
    .input-btn.send { background: var(--accent); color: #fff; }
    .input-btn.send:hover { background: var(--accent-hover); }
    .input-btn.send:disabled { opacity: 0.4; cursor: default; }
    .input-btn svg { width: 20px; height: 20px; }

    .conn-banner {
      padding: 6px 16px; font-size: 11px; text-align: center;
      color: #fff; flex-shrink: 0;
    }
    .conn-banner.connected { background: #22c55e; }
    .conn-banner.disconnected { background: #ef4444; }
    .conn-banner.reconnecting { background: #f59e0b; }

    .drag-overlay {
      position: absolute; inset: 0;
      background: rgba(0,0,0,0.5);
      backdrop-filter: blur(4px);
      border-radius: 16px;
      display: flex; align-items: center; justify-content: center;
      z-index: 10; color: #fff; font-size: 16px; font-weight: 500;
    }

    @media (max-width: 480px) {
      .window {
        width: 100vw; max-width: 100vw;
        height: 100dvh; max-height: 100dvh;
        bottom: 0; right: 0; left: 0;
        border-radius: 0; border: none;
      }
      .wrapper { bottom: 0; right: 0; left: 0; }
      .toggle {
        position: fixed;
        bottom: var(--pos-bottom);
        right: var(--pos-right);
        left: var(--pos-left, auto);
      }
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this._initMessages();
    if (this.open) this._ensureSession();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._disconnectWebSocket();
  }

  updated(changed) {
    if (changed.has('open') && this.open) {
      this._unreadCount = 0;
      this._ensureSession();
      this._scrollToBottom();
    }
    if (changed.has('primaryColor') || changed.has('bgColor') || changed.has('textColor')
      || changed.has('userBubbleColor') || changed.has('botBubbleColor')) {
      this._applyColors();
    }
  }

  _applyColors() {
    const colors = {
      '--ai-chat-primary': this.primaryColor,
      '--ai-chat-bg': this.bgColor,
      '--ai-chat-text': this.textColor,
      '--ai-chat-user-bubble': this.userBubbleColor,
      '--ai-chat-bot-bubble': this.botBubbleColor,
    };
    for (const [prop, value] of Object.entries(colors)) {
      const v = typeof value === 'string' ? value.trim() : '';
      if (!v) continue;
      let ok = false;
      try { ok = typeof CSS !== 'undefined' && CSS.supports('color', v); } catch { ok = false; }
      if (ok) this.style.setProperty(prop, v);
    }
  }

  /* ─── Sessione (REST) ─── */

  _readStoredSessionId() {
    try {
      const value = sessionStorage.getItem('chat.sessionid');
      return typeof value === 'string' && value ? value : null;
    } catch { return null; }
  }

  _storeSessionId(sessionId) {
    try { sessionStorage.setItem('chat.sessionid', sessionId); } catch { /* storage non disponibile */ }
  }

  _ensureSession() {
    if (this._sessionId) return Promise.resolve(this._sessionId);
    if (this._sessionPromise) return this._sessionPromise;
    const stored = this._readStoredSessionId();
    if (stored) {
      this._sessionId = stored;
      return Promise.resolve(stored);
    }
    const promise = fetch(this._apiUrl('/api/chat/session'), { method: 'POST' })
      .then((resp) => {
        if (!resp.ok) throw new Error('HTTP ' + resp.status);
        return resp.json();
      })
      .then((data) => {
        const sessionId = data && data.sessionid;
        if (typeof sessionId !== 'string' || !sessionId) {
          throw new Error('sessionid mancante nella risposta');
        }
        this._sessionId = sessionId;
        this._storeSessionId(sessionId);
        return sessionId;
      })
      .finally(() => { this._sessionPromise = null; });
    promise.catch(() => {});
    this._sessionPromise = promise;
    return promise;
  }

  /* ─── URL ─── */

  _envBase(name) {
    try { return import.meta.env?.[name] || ''; } catch { return ''; }
  }

  _apiUrl(path) {
    const base = (this.apiUrl || this._envBase('VITE_AI_CHAT_API_BASE') || '').trim().replace(/\/+$/, '');
    if (!base) return path;
    if (/^https?:\/\//i.test(base)) return base + path;
    return (base.startsWith('/') ? '' : '/') + base + path;
  }

  _wsUrl(sessionId) {
    const base = (this.wsUrl || this._envBase('VITE_AI_CHAT_WS_BASE') || '').trim().replace(/\/+$/, '');
    let url;
    if (/^wss?:\/\//i.test(base)) {
      url = base;
    } else {
      const scheme = location.protocol === 'https:' ? 'wss://' : 'ws://';
      url = scheme + location.host + (base ? (base.startsWith('/') ? '' : '/') + base : '');
    }
    if (!/\/chat\/?$/.test(url)) url += '/chat/';
    return url + (url.includes('?') ? '&' : '?') + 'sessionid=' + encodeURIComponent(sessionId);
  }

  /* ─── WebSocket ─── */

  _connectWebSocket(sessionId) {
    if (this._ws && this._ws.readyState === WebSocket.OPEN) return Promise.resolve(this._ws);
    if (this._wsOpenPromise) return this._wsOpenPromise;
    this._disconnectWebSocket();
    this._wsUsed = true;
    this._wsState = 'connecting';
    let ws;
    try {
      ws = new WebSocket(this._wsUrl(sessionId));
    } catch (err) {
      this._wsState = 'closed';
      return Promise.reject(err);
    }
    this._ws = ws;
    this._wsOpenPromise = new Promise((resolve, reject) => {
      let settled = false;
      this._wsOpenReject = reject;
      ws.onopen = () => {
        this._wsOpenPromise = null;
        this._wsOpenReject = null;
        this._wsState = 'open';
        if (!settled) { settled = true; resolve(ws); }
      };
      ws.onmessage = (e) => this._handleWsFrame(e.data);
      ws.onclose = () => {
        if (this._ws === ws) this._ws = null;
        this._wsOpenPromise = null;
        this._wsOpenReject = null;
        this._wsState = 'closed';
        this._isTyping = false;
        if (!settled) { settled = true; reject(new Error('connessione WebSocket chiusa')); }
      };
      ws.onerror = () => {
        if (!settled) {
          settled = true;
          this._wsOpenPromise = null;
          this._wsOpenReject = null;
          this._wsState = 'closed';
          this._isTyping = false;
          reject(new Error('errore di connessione WebSocket'));
        }
      };
    });
    return this._wsOpenPromise;
  }

  _disconnectWebSocket() {
    const ws = this._ws;
    const reject = this._wsOpenReject;
    this._ws = null;
    this._wsOpenPromise = null;
    this._wsOpenReject = null;
    if (this._wsState !== 'idle') this._wsState = 'closed';
    if (ws) {
      ws.onopen = null;
      ws.onmessage = null;
      ws.onclose = null;
      ws.onerror = null;
      try { ws.close(); } catch { /* socket gia chiusa */ }
    }
    if (reject) reject(new Error('connessione WebSocket interrotta'));
  }

  _handleWsFrame(raw) {
    let msg = null;
    try {
      msg = JSON.parse(raw);
    } catch {
      console.warn('[ai-chat] frame ignorato: payload non JSON');
      return;
    }
    if (!msg || typeof msg !== 'object' || Array.isArray(msg)) {
      console.warn('[ai-chat] frame ignorato: formato inatteso');
      return;
    }
    this._handleWsMessage(msg);
  }

  _handleWsMessage(msg) {
    if (msg.type === 'typing' || msg.isTyping !== undefined) {
      const data = msg.data && typeof msg.data === 'object' && !Array.isArray(msg.data) ? msg.data : null;
      this._isTyping = (data ? data.isTyping : undefined) ?? msg.isTyping ?? false;
      return;
    }
    if (msg.type !== 'message') return;
    const data = msg.data && typeof msg.data === 'object' && !Array.isArray(msg.data) ? msg.data : {};
    const nested = data.content && typeof data.content === 'object' && !Array.isArray(data.content) ? data.content : null;
    const text = typeof data.text === 'string' ? data.text
                : typeof data.content === 'string' ? data.content
                : nested && typeof nested.text === 'string' ? nested.text
                : '';
    let elements = data.elements ?? (nested ? nested.elements : undefined) ?? [];
    if (!Array.isArray(elements)) elements = [];
    if (text || elements.length > 0) this._addBotMessage(text, elements);
  }

  _initMessages() {
    if (!this.initialMessages) return;
    try {
      const parsed = JSON.parse(this.initialMessages);
      if (Array.isArray(parsed)) {
        this._messages = parsed.map(m => ({
          id: m.id || crypto.randomUUID(),
          role: m.role || 'bot',
          content: m.content || m.text || '',
          timestamp: m.timestamp || Date.now(),
          elements: m.elements,
          files: m.files || (m.elements || []).filter(e =>
            e.type === 'IMAGE' || e.type === 'DOCUMENT'
          ),
          status: m.status || 'sent',
        }));
      }
    } catch {}
  }

  _addBotMessage(text, elements) {
    const id = crypto.randomUUID();
    if (this._messageIds.has(id)) return;
    const els = Array.isArray(elements) ? elements.filter(e => e && typeof e === 'object') : [];
    const files = els.filter(e => e.type === 'IMAGE' || e.type === 'DOCUMENT');
    this._messageIds.add(id);
    this._messages = [...this._messages, {
      id, role: 'bot', content: text || '',
      elements: els,
      files,
      timestamp: Date.now(), status: 'sent',
    }];
    this._isTyping = false;
    if (!this.open) this._unreadCount++;
    this._scrollToBottom();
  }

  _addUserMessage(content) {
    const id = crypto.randomUUID();
    const files = [...this._files];
    this._messages = [...this._messages, {
      id, role: 'user', content, files,
      timestamp: Date.now(), status: 'sent',
    }];
    this._files = [];
    this._inputValue = '';
    const ta = this.shadowRoot?.querySelector('textarea');
    if (ta) ta.style.height = 'auto';
    this._scrollToBottom();
    this._sendToServer(content);
  }

  async _sendToServer(content) {
    let sessionId;
    try {
      sessionId = await this._ensureSession();
    } catch {
      console.warn('[ai-chat] sessione non disponibile, messaggio non inviato');
      this._markLastMessageError();
      return;
    }
    try {
      const ws = await this._connectWebSocket(sessionId);
      ws.send(JSON.stringify({ type: 'message', data: { content } }));
    } catch {
      console.warn('[ai-chat] WebSocket non disponibile, messaggio non inviato');
      this._markLastMessageError();
    }
  }

  _markLastMessageError() {
    this._messages = this._messages.map((m, i) =>
      i === this._messages.length - 1 ? { ...m, status: 'error' } : m
    );
  }

  _handleQuickReply(text) {
    this._addUserMessage(text);
  }

  _handleSubmit() {
    const val = this._inputValue.trim();
    if (!val) return;
    this._addUserMessage(val);
  }

  _handleInput(e) {
    const target = e.target;
    this._inputValue = target.value;
    target.style.height = 'auto';
    target.style.height = Math.min(target.scrollHeight, 120) + 'px';
  }

  _handleKeydown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this._handleSubmit();
    }
  }

  _handleFileChange(e) {
    const files = Array.from(e.target.files || []);
    this._addFiles(files);
    e.target.value = '';
  }

  _addFiles(files) {
    const allowed = this.allowedFileTypes ? this.allowedFileTypes.split(',').map(t => t.trim()) : null;
    for (const file of files) {
      if (file.size > this.maxFileSize * 1024 * 1024) continue;
      if (allowed && !allowed.some(t => file.type.includes(t.replace('*', '').replace('/', '')))) continue;
      if (this._files.some(f => f.name === file.name && f.size === file.size)) continue;
      const reader = new FileReader();
      reader.onload = (e) => {
        this._files = [...this._files, {
          name: file.name, size: file.size, type: file.type,
          url: e.target.result || '', file,
        }];
      };
      reader.readAsDataURL(file);
    }
  }

  _removeFile(index) {
    this._files = this._files.filter((_, i) => i !== index);
  }

  _handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    this._addFiles(Array.from(e.dataTransfer?.files || []));
  }

  _toggle() {
    this.open = !this.open;
    if (this.open) {
      this._unreadCount = 0;
      this.dispatchEvent(new CustomEvent('chat-open', { bubbles: true, composed: true }));
      setTimeout(() => {
        const ta = this.shadowRoot?.querySelector('textarea');
        if (ta) ta.focus();
      }, 300);
    } else {
      this.dispatchEvent(new CustomEvent('chat-close', { bubbles: true, composed: true }));
    }
  }

  _scrollToBottom() {
    requestAnimationFrame(() => {
      const body = this.shadowRoot?.querySelector('.messages');
      if (body) body.scrollTop = body.scrollHeight;
    });
  }

  _handleScroll(e) {
    const el = e.target;
    this._isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  }

  _formatTime(ts) {
    return new Date(ts).toLocaleTimeString(this._locale(), { hour: '2-digit', minute: '2-digit' });
  }

  /* ─── i18n ─── */

  _locale() {
    const candidates = [];
    if (this.lang) candidates.push(this.lang);
    try {
      const docLang = document.documentElement.lang;
      if (docLang) candidates.push(docLang);
    } catch { /* no document */ }
    try {
      const nav = navigator.languages || (navigator.language ? [navigator.language] : []);
      candidates.push(...nav);
    } catch { /* no navigator */ }
    for (const c of candidates) {
      if (!c) continue;
      const tag = String(c).trim().toLowerCase().split('-')[0];
      if (LABELS[tag]) return tag;
    }
    return 'en';
  }

  _customLabels() {
    if (!this.labels) return null;
    try {
      const parsed = JSON.parse(this.labels);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
    } catch { /* labels non validi: ignorati */ }
    return null;
  }

  _t(key, params) {
    const dict = LABELS[this._locale()] || LABELS.en;
    const custom = this._customLabels();
    let label = (custom && key in custom ? custom[key] : undefined)
      ?? dict[key]
      ?? LABELS.en[key]
      ?? key;
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        label = String(label).replaceAll('{' + k + '}', String(v));
      }
    }
    return label;
  }

  _formatSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  _isImage(type) {
    return type.startsWith('image/') || type === 'IMAGE';
  }

  _renderMessageContent(content) {
    if (typeof content !== 'string') {
      content = content?.text || String(content) || '';
    }
    const escaped = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    const formatted = escaped
      .replace(/\n/g, '<br>')
      .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
    return html`<p>${unsafeHTML(formatted)}</p>`;
  }

  render() {
    const hasMessages = this._messages.length > 0;

    return html`
      <div class="wrapper" role="region" aria-label=${this._t('chatRegion')}>
        ${when(!this.open, () => html`
          <button class="toggle" @click=${this._toggle} aria-label=${this._t('openChat')} title=${this._t('openChat')}>
            <span class="icon-closed">${ICONS.chat}</span>
            <span class="icon-open">${ICONS.close}</span>
            ${when(this._unreadCount > 0, () => html`
              <span class="badge" role="status" aria-live="polite">
                ${this._unreadCount > 9 ? '9+' : this._unreadCount}
              </span>
            `)}
          </button>
        `)}

        ${when(this.open, () => html`
          <div class="window" role="dialog" aria-label=${this._t('chatDialog')}>
            ${when(this._wsState === 'closed', () => html`
              <div class="conn-banner disconnected">${this._t('disconnected')}</div>
            `)}

            <div class="header">
              <div class="header-avatar">
                ${this.botAvatar
                  ? html`<img src=${this.botAvatar} alt=${this.botName} crossorigin="anonymous">`
                  : html`<span>${this.botName.charAt(0)}</span>`
                }
              </div>
              <div class="header-info">
                <div class="header-title">
                  <div class="header-name">${this.botName}</div>
                  <span class="ai-badge" role="img" aria-label=${this._t('aiBadgeTitle')} title=${this._t('aiBadgeTitle')}>${ICONS.ai}AI</span>
                </div>
                <div class="header-status">
                  <span class="dot ${this._isTyping ? 'typing' : (this._wsState === 'open' ? 'online' : 'offline')}"></span>
                  ${this._isTyping ? this._t('typing') : (this._wsState === 'open' ? this._t('online') : (this._wsState === 'connecting' ? this._t('connecting') : (this._wsState === 'closed' ? this._t('offline') : this._t('ready'))))}
                </div>
              </div>
              <div class="header-actions">
                <button class="header-btn" @click=${this._toggle} aria-label=${this._t('closeChat')} title=${this._t('closeChat')}>
                  ${ICONS.close}
                </button>
              </div>
            </div>

            <div class="messages" @scroll=${this._handleScroll} role="log" aria-live="polite" aria-label=${this._t('messagesLog')}>
              ${when(!hasMessages, () => html`
                <div class="empty-state">
                  ${ICONS.chat}
                  <h3>${this.botName}</h3>
                  <p>${this._t('emptyText')}</p>
                </div>
              `)}

              ${repeat(this._messages, (m) => m.id, (m) => html`
                <div class="msg-group ${m.role}" data-ai-generated=${m.role === 'bot' ? 'true' : nothing}>
                  <div class="msg-bubble">
                    ${m.content ? this._renderMessageContent(m.content) : nothing}
                    ${when(m.elements && m.elements.length > 0, () => html`
                      <div class="msg-elements">
                        ${m.elements.map(e => {
                          const t = (e.type || e.itemtype || '').toLowerCase();
                          if (t === 'web_link' || t === 'url') {
                            const url = e.value || '';
                            let domain = '';
                            try { domain = new URL(url).hostname.replace(/^www\./, ''); } catch {}
                            return html`
                              <a class="msg-element msg-element--web-link" href=${url} target="_blank" rel="noopener noreferrer">
                                ${domain ? html`<span class="msg-element__domain">${domain}</span>` : nothing}
                                <span class="msg-element__label">${e.displayText || url}</span>
                              </a>`;
                          }
                          if (t === 'document' || t === 'doc') {
                            return html`
                              <a class="msg-element msg-element--document" href=${e.value} download target="_blank" rel="noopener noreferrer">
                                ${ICONS.file} ${e.displayText || e.value}
                              </a>`;
                          }
                          if (t === 'image' || t === 'img') {
                            return html`
                              <a class="msg-element msg-element--image" href=${e.value} target="_blank" rel="noopener noreferrer">
                                <img src=${e.value} alt=${e.displayText || 'image'} loading="lazy">
                              </a>`;
                          }
                          if (t === 'quick_reply' || t === 'button') {
                            return html`
                              <button class="msg-element msg-element--quick-reply" @click=${() => this._handleQuickReply(e.displayText || e.value)}>
                                ${e.displayText || e.value}
                              </button>`;
                          }
                          if (t === 'system_action' || t === 'system') {
                            return nothing;
                          }
                          return nothing;
                        })}
                      </div>
                    `)}
                  </div>
                  <div class="msg-status ${m.status}">
                    ${m.status === 'error' ? ICONS.error : (m.status === 'sent' ? ICONS.check : nothing)}
                    ${m.status === 'error' ? this._t('error') : ''}
                  </div>
                  <div class="msg-time">${this._formatTime(m.timestamp)}</div>
                </div>
              `)}

              ${when(this._isTyping, () => html`
                <div class="typing">
                  <div class="typing-dots"><span></span><span></span><span></span></div>
                  <span class="typing-label">${this.botName} ${this._t('typing')}</span>
                </div>
              `)}
            </div>

            ${when(!this._isAtBottom && hasMessages, () => html`
              <button class="scroll-btn visible" @click=${this._scrollToBottom} aria-label=${this._t('scrollToBottom')}>
                ${ICONS.arrowDown}
              </button>
            `)}

            <div class="ai-disclosure">
              ${ICONS.ai}
              <span>${this._t('aiDisclosure')}</span>
            </div>

            <div class="input-area" @dragover=${(e) => { e.preventDefault(); e.stopPropagation(); }} @drop=${this._handleDrop}>
              ${when(this._files.length > 0, () => html`
                <div class="file-previews">
                  ${this._files.map((f, i) => html`
                    <div class="file-chip">
                      ${this._isImage(f.type)
                        ? html`<img src=${f.url} alt=${f.name}>`
                        : html`<span class="file-icon">${ICONS.file}</span>`
                      }
                      <div class="file-info">
                        <div class="file-name">${f.name}</div>
                        <div class="file-size">${this._formatSize(f.size)}</div>
                      </div>
                      <button class="file-remove" @click=${() => this._removeFile(i)} aria-label=${this._t('removeFile', { file: f.name })}>✕</button>
                    </div>
                  `)}
                </div>
              `)}

              <div class="input-row">
                <textarea .value=${this._inputValue} @input=${this._handleInput} @keydown=${this._handleKeydown}
                  placeholder=${this.placeholder || this._t('messagePlaceholder')} rows="1" aria-label=${this._t('messageLabel')}></textarea>
                <div class="input-actions">
                 <!-- <button class="input-btn" @click=${() => { this.shadowRoot?.getElementById('file-input')?.click(); }} aria-label=${this._t('attach')} title=${this._t('attach')}>${ICONS.attach}</button> -->
                  <button class="input-btn send" @click=${this._handleSubmit} ?disabled=${!this._inputValue.trim()} aria-label=${this._t('send')} title=${this._t('send')}>${ICONS.send}</button>
                </div>
              </div>
              <input id="file-input" type="file" multiple accept=${this.allowedFileTypes || '*/*'} @change=${this._handleFileChange} style="display:none">
            </div>
          </div>
        `)}
      </div>
    `;
  }
}

customElements.define('ai-chat', AiChat);

export default AiChat;
